import React, { useMemo, useState } from 'react';
import { datetime, type DatetimeType } from '@common/services/datetime';
import { Button, Select } from 'antd';
import { useMutation, useQuery } from '@common/hooks';
import {
  Checkbox,
  DatePicker,
  Divider,
  LoadingState,
  Icon,
  Form,
  Col,
  Row,
  Text,
} from '@common/components';
import {
  AddHoursFormOptions,
  ManagerHourBlockErrorsPreview,
  ManagerUpsertHours,
} from '@timekeeping/graphql';
import {
  atFacilityZone,
  calculateTotalSeconds,
  findById,
  isOutsideDateRange,
  maybeParseInTz,
  withMinutesPrecision,
} from '@timekeeping/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import { DrawerBody, DrawerFooter } from '@pay-bill/components';
import {
  ReviewWorkerTimesheetFragment,
  ReviewHourBlockFragment,
  WorksTimekeepingAddHoursInput,
} from '@common/graphql/schema';

type FormValues = {
  facilityId: string | undefined;
  unitId: string | undefined;
  hourTypeId: string | undefined;
  clockIn?: DatetimeType;
  clockOut?: DatetimeType;
  skippedLunchBreak: boolean | undefined;
  breakStart?: DatetimeType;
  breakEnd?: DatetimeType;
};

export const HourBlockForm = ({
  workerTimesheet,
  hourBlock,
  action,
  onSuccess,
}: {
  workerTimesheet?: ReviewWorkerTimesheetFragment | undefined;
  hourBlock?: ReviewHourBlockFragment | undefined;
  action: 'edit' | 'add' | 'restore';
  onSuccess?: () => void;
}) => {
  const [form] = Form.useForm();
  const [noBreak, setNoBreak] = useState(hourBlock?.skippedLunchBreak);
  const [touched, setTouched] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>(() => ({
    facilityId: hourBlock?.facility!.id,
    unitId: hourBlock?.unit!.id,
    hourTypeId: hourBlock?.hourType!.id,
    clockIn: maybeParseInTz(
      hourBlock?.inOut.startedAt,
      hourBlock?.inOut.startedAtTz
    ),
    clockOut: maybeParseInTz(
      hourBlock?.inOut.endedAt,
      hourBlock?.inOut.endedAtTz
    ),
    skippedLunchBreak: hourBlock?.skippedLunchBreak,
    breakStart: maybeParseInTz(
      hourBlock?.lunchBreak?.startedAt,
      hourBlock?.lunchBreak?.startedAtTz
    ),
    breakEnd: maybeParseInTz(
      hourBlock?.lunchBreak?.endedAt,
      hourBlock?.lunchBreak?.endedAtTz
    ),
  }));

  const effectiveWorkerTs = (hourBlock?.workerTimesheet || workerTimesheet)!;
  const optionsQuery = useQuery(AddHoursFormOptions, {
    fetchPolicy: 'cache-and-network',
    variables: {
      workerTimesheetId: effectiveWorkerTs.id,
      facilityId: formValues.facilityId,
    },
  });

  const formOptions = optionsQuery.data?.worksTimekeepingAddHoursOptions;

  const selectedFacility = formValues.facilityId
    ? findById(formOptions?.facilities || [], formValues.facilityId)!
    : undefined;

  const facilityZone = selectedFacility?.timeZone;
  const submitted = {
    clockIn: atFacilityZone(formValues.clockIn, facilityZone),
    clockOut: atFacilityZone(formValues.clockOut, facilityZone),
    breakStart: atFacilityZone(formValues.breakStart, facilityZone),
    breakEnd: atFacilityZone(formValues.breakEnd, facilityZone),
  };

  const hourBlockInput: WorksTimekeepingAddHoursInput = {
    entries: [
      {
        id: hourBlock?.inOut.id,
        entryType: 'in_out' as const,
        startedAt: submitted.clockIn?.toISOString(),
        startedAtTz: facilityZone,
        endedAt: submitted.clockOut?.toISOString(),
        endedAtTz: facilityZone,
      },
      {
        id: hourBlock?.lunchBreak?.id,
        entryType: 'lunch_break' as const,
        startedAt: submitted.breakStart?.toISOString(),
        startedAtTz: facilityZone,
        endedAt: submitted.breakEnd?.toISOString(),
        endedAtTz: facilityZone,
      },
    ],
    skippedLunchBreak: noBreak,
    hourBlockId: hourBlock?.id,
    hourTypeId: formValues.hourTypeId!,
    facilityId: formValues.facilityId!,
    unitId: formValues.unitId!,
    workerTimesheetId: effectiveWorkerTs?.id,
    timesheetId: effectiveWorkerTs.timesheet.id,
  };

  const addFormFieldsFilledIn = (formValues: FormValues) => {
    return !!(
      formValues?.facilityId &&
      formValues?.unitId &&
      formValues?.hourTypeId &&
      formValues?.clockIn &&
      formValues?.clockOut &&
      (formValues?.skippedLunchBreak ||
        (formValues?.breakStart && formValues?.breakEnd))
    );
  };

  const skipErrorValidation = (action: string) => {
    if (action === 'edit') {
      return !touched;
    }

    if (action === 'add') {
      return !addFormFieldsFilledIn(formValues);
    }

    if (action === 'restore') {
      return false;
    }
  };

  const validations = useQuery(ManagerHourBlockErrorsPreview, {
    fetchPolicy: 'cache-and-network',
    skip: skipErrorValidation(action),
    variables: {
      input: hourBlockInput,
    },
  });

  const errorsPreview = validations.data?.preview;
  const errors = errorsPreview?.errors;

  const toggleBreak = (value: boolean) => {
    form.setFieldsValue({ breakStart: undefined, breakEnd: undefined });
    setFormValues((values) => ({
      ...values,
      breakStart: undefined,
      breakEnd: undefined,
      skippedLunchBreak: value,
    }));
    setNoBreak(value);
  };

  const [upsertHoursMutation, upsertMutationData] = useMutation(
    ManagerUpsertHours,
    {
      onCompleted: () => {
        onSuccess?.();
        createMessage(
          'success',
          action === 'restore' ? 'Hours restored.' : `Hours ${action}ed.`
        );
      },
      onError: () =>
        createMessage('error', `Failed to ${action} hours. Please try again.`),
    }
  );

  const onSubmit = () => {
    upsertHoursMutation({
      variables: {
        input: hourBlockInput,
      },
    });
  };

  const { minStart, maxEnd } = useMemo(() => {
    if (!formOptions) {
      return { minStart: undefined, maxEnd: undefined };
    }

    return {
      minStart: datetime(formOptions.clockInRange.minimum),
      maxEnd: datetime(formOptions.clockInRange.maximum),
    };
  }, [formOptions]);

  const disabledDate = (date: DatetimeType) =>
    isOutsideDateRange(date, minStart, maxEnd);

  const formValid = (action: string) => {
    if (action === 'restore') {
      return errorsPreview?.preventsChanges === false;
    }

    return (
      touched &&
      !validations.loading &&
      errorsPreview?.preventsChanges === false
    );
  };

  if (optionsQuery.loading && !optionsQuery.data) {
    return <LoadingState />;
  }

  return (
    <>
      <DrawerBody>
        {action === 'restore' && (
          <span>Review details and save to restore these hours.</span>
        )}
        <Form
          className="hour-block-form"
          onFinish={onSubmit}
          layout="vertical"
          form={form}
          initialValues={formValues}
          onValuesChange={(changes) => {
            setTouched(true);
            setFormValues((values) => {
              const newState = { ...values, ...changes };

              newState.clockIn = withMinutesPrecision(newState.clockIn);
              newState.clockOut = withMinutesPrecision(newState.clockOut);
              newState.breakStart = withMinutesPrecision(newState.breakStart);
              newState.breakEnd = withMinutesPrecision(newState.breakEnd);

              return newState;
            });
          }}
        >
          <Row
            gutter={[0, 20]}
            align="middle"
            style={{
              marginTop: action === 'restore' ? '24px' : '0',
              marginBottom: '6px',
            }}
          >
            <Text bold size="lg">
              Location
            </Text>
          </Row>
          <Row gutter={[0, 16]} align="middle">
            <Col span={6}>
              <Text secondary>Facility</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="facilityId"
                validateStatus={errors?.facility?.[0] ? 'error' : ''}
                help={errors?.facility?.[0]?.message}
              >
                <Select
                  showSearch
                  className="facility-select"
                  size="large"
                  optionFilterProp="children"
                  placeholder="Search facilities"
                  disabled={optionsQuery.loading}
                  suffixIcon={<Icon icon="search" kind="light" size="sm" />}
                  onChange={() => {
                    form.setFieldsValue({ unitId: undefined });
                    setFormValues((value) => ({
                      ...value,
                      unitId: undefined,
                    }));
                  }}
                >
                  {formOptions?.facilities.map((facility, id) => (
                    <Select.Option
                      key={id}
                      value={facility.id}
                      title={facility.name}
                    >
                      {facility.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Text secondary>Unit / Cost Center</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="unitId"
                validateStatus={errors?.unit?.[0] ? 'error' : ''}
                help={errors?.unit?.[0]?.message}
              >
                <Select
                  showSearch
                  className="unit-cost-center-select"
                  size="large"
                  optionFilterProp="children"
                  placeholder="Search units / cost centers"
                  disabled={
                    formValues.facilityId === null || optionsQuery.loading
                  }
                  suffixIcon={<Icon icon="search" kind="light" size="sm" />}
                >
                  {formOptions?.units.map((unit, id) => (
                    <Select.Option key={id} value={unit.id} title={unit.name}>
                      {`${unit.name} (${unit.costCenter})`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Divider className="hour-block-drawer-divider" />
          <Row gutter={[0, 16]} align="middle">
            <Col span={24}>
              <Text bold size="lg">
                Hours
              </Text>
            </Col>
            <Col span={6}>
              <Text secondary>Hour Type</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="hourTypeId"
                validateStatus={errors?.hourType?.[0] ? 'error' : ''}
                help={errors?.hourType?.[0]?.message}
              >
                <Select
                  className="select-width hour-type-select"
                  size="large"
                  optionFilterProp="children"
                  placeholder="Select an hour type"
                  disabled={optionsQuery.loading}
                >
                  {formOptions?.hourTypes.map((hourType, id) => (
                    <Select.Option
                      key={id}
                      value={hourType.id}
                      title={hourType.name}
                    >
                      <span
                        style={{ height: 'auto', whiteSpace: 'break-spaces' }}
                      >
                        {hourType.name}
                      </span>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text secondary>Clock In</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="clockIn"
                validateStatus={errors?.inOut?.startedAt?.[0] ? 'error' : ''}
                help={errors?.inOut?.startedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  name="picker-clock-in"
                  size="large"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text secondary>Clock Out</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="clockOut"
                validateStatus={errors?.inOut?.endedAt?.[0] ? 'error' : ''}
                help={errors?.inOut?.endedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  size="large"
                  name="picker-clock-out"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text secondary>Lunch / Break Start</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="breakStart"
                validateStatus={
                  errors?.lunchBreak?.startedAt?.[0] ? 'error' : ''
                }
                help={errors?.lunchBreak?.startedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  size="large"
                  name="picker-break-start"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  disabled={noBreak || !formValues.clockIn}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="skippedLunchBreak">
                <Checkbox
                  onChange={(e) => toggleBreak(e.target.checked)}
                  checked={noBreak}
                >
                  No break
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Text secondary>Lunch / Break End</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="breakEnd"
                validateStatus={errors?.lunchBreak?.endedAt?.[0] ? 'error' : ''}
                help={errors?.lunchBreak?.endedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  size="large"
                  name="picker-break-end"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  disabled={noBreak || !formValues.clockIn}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text secondary>Total Time</Text>
            </Col>
            <Col span={18} className="total-time-col">
              {calculateTotalSeconds(
                submitted.clockIn,
                submitted.clockOut,
                submitted.breakStart,
                submitted.breakEnd
              )}
            </Col>
            <Col span={24} className="time-zone-col">
              <Text>
                *All times in{' '}
                {submitted.clockIn && facilityZone
                  ? submitted.clockIn.format('z')
                  : 'UTC'}
              </Text>
            </Col>
            {hourBlock?.notes && (
              <>
                <Col span={24}>
                  <Divider className="hour-block-drawer-divider" />
                </Col>
                <Col span={24}>
                  <Text bold size="lg">
                    Worker Note
                  </Text>
                </Col>
                <Col span={24}>
                  <Text className="hour-block-note">{hourBlock?.notes}</Text>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </DrawerBody>
      <DrawerFooter>
        <Col span={9}>
          <Button
            className="submit-hour-block-button"
            type="primary"
            size="large"
            disabled={!formValid(action)}
            onClick={form.submit}
            loading={upsertMutationData.loading}
          >
            {action === 'edit' || action === 'restore' ? 'Save' : 'Add Hours'}
          </Button>
        </Col>
      </DrawerFooter>
      <style jsx>{`
        :global(.HourBlockDrawer .ant-form-item) {
          margin-bottom: 0;
        }
        :global(.HourBlockDrawer .ant-drawer-body) {
          padding: 0;
        }
        :global(.HourBlockDrawer .drawer-body) {
          height: 100%;
        }
        :global(.HourBlockDrawer .drawer-footer) {
          position: absolute;
          bottom: 0;
          width: 100%;
          background-color: white;
        }
        :global(.HourBlockDrawer .hour-block-form) {
          margin-bottom: 80px;
        }
        :global(.ant-select-dropdown .ant-select-item-option-content) {
          white-space: break-spaces;
        }
        :global(.select-width) {
          width: 100%;
        }
        :global(.hour-block-note) {
          padding-top: 8px;
          padding-bottom: 16px;
        }
        :global(.hour-block-drawer-divider) {
          margin: 16px 0 !important;
        }
      `}</style>
    </>
  );
};
